import { useEffect, useRef, useState } from "react";

export type ActivePhase = "idle" | "entering" | "active" | "leaving";

// Tracks transitions of `isActive` so a section can run distinct entrance
// and exit animations:
// - mount or false → true: phase becomes "entering" for `durationMs`,
//   then "active".
// - true → false: phase becomes "leaving" for `durationMs`, then "idle".
// First mount with isActive=true counts as an entrance, so the very first
// section in the shade gets its entrance animation too (without this, the
// initial state would jump straight to "active" and skip the rise-in).
export const useActivePhase = (
  isActive: boolean,
  durationMs = 1000,
): ActivePhase => {
  const [phase, setPhase] = useState<ActivePhase>(
    isActive ? "entering" : "idle",
  );
  // Treat the initial mount as a transition from "not active" so the
  // first-mount-active case fires "entering" → "active".
  const prevRef = useRef(false);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = isActive;
    if (isActive && !prev) {
      setPhase("entering");
      const t = setTimeout(() => setPhase("active"), durationMs);
      return () => clearTimeout(t);
    }
    if (!isActive && prev) {
      setPhase("leaving");
      const t = setTimeout(() => setPhase("idle"), durationMs);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isActive, durationMs]);

  return phase;
};
